import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Shield, Eye, EyeOff, Mail, Lock, Server } from 'lucide-react'
import vestirstudioLogo from '../../images/vestirstudio_logo.png'

export const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/'

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim()) {
      setError('Informe o e-mail')
      return
    }
    if (!password.trim()) {
      setError('Informe a senha')
      return
    }

    setError('')
    setLoading(true)

    try {
      const result = await login(email, password)
      
      if (result.success) {
        navigate(result.user?.must_change_password ? '/account/password' : from, { replace: true })
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-admin-900 via-admin-800 to-admin-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-20 w-20 rounded-full shadow-lg overflow-hidden">
            <img
              src={vestirstudioLogo}
              alt="Logo da VestirStudio"
              className="h-full w-full object-cover"
              decoding="async"
              loading="eager"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-white">
            VestirStudio
          </h2>
          <p className="mt-2 text-center text-sm text-admin-300">
            Console de Administração da VestirStudio
          </p>
          <div className="mt-4 flex items-center justify-center text-admin-400">
            <Server className="h-4 w-4 mr-2" />
            <span className="text-xs">Sistema de Gestão Multiestúdio</span>
          </div>
        </div>
        
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-admin-200 p-8">
          <form 
            className="space-y-6" 
            onSubmit={(e) => {
              e.preventDefault() // Prevent default form submission
              handleSubmit(e)
            }}
          >
            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-admin-800 mb-2">
                  E-mail do administrador
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-admin-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-10"
                    placeholder="admin@vestirstudio.com"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-admin-800 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-admin-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10 pr-12"
                    placeholder="Digite a senha de administrador"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-admin-400 hover:text-admin-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full py-3 text-base font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Autenticando...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Shield className="h-5 w-5 mr-2" />
                    Acessar o console de administração
                  </div>
                )}
              </button>
            </div>
            
            <div className="text-center">
              <p className="text-xs text-admin-500">
                Esta é uma área restrita. O acesso não autorizado é proibido.
              </p>
            </div>
          </form>
        </div>

        {/* System Info */}
        <div className="text-center text-xs text-admin-400 space-y-1">
          <p>Plataforma de Administração VestirStudio v1.0.0</p>
          <p>Administração SaaS Multiestúdio</p>
        </div>
      </div>
    </div>
  )
} 